<template>
    <Modal
        :value="value"
        :mask-closable="false"
        :width="720"
        :footer-hide="true"
        @input="v => $emit('input', v)"
        @on-cancel="onCancel"
    >
        <div slot="header" class="tw-flex tw-items-center tw-gap-2">
            <div
                class="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-font-semibold tw-flex-shrink-0"
                :style="{ background: avatarColor }"
            >
                {{ bankInitial }}
            </div>
            <div class="tw-flex tw-flex-col">
                <span class="tw-font-semibold tw-text-base tw-leading-tight">
                    {{ isEdit ? $t('bankProfile.editAccount') : $t('bankProfile.addAccount') }}
                </span>
                <span class="tw-text-gray-500 tw-text-xs">
                    {{ $t('bankProfile.addingTo') }}: <b>{{ bankName }}</b>
                </span>
            </div>
        </div>

        <div v-if="value" @keydown.ctrl.enter.stop="onSaveAndAdd" @keydown.meta.enter.stop="onSaveAndAdd">
            <div class="row">
                <!-- Left: Employee (primary) -->
                <div class="col-md-5 tw-pr-2">
                    <label class="form-col-label control-label tw-block tw-mb-1">
                        {{ $t('bankProfile.employeeId') }}
                    </label>
                    <Select
                        ref="employeeSelect"
                        v-model="form.employee_id"
                        filterable
                        clearable
                        transfer
                        size="large"
                        :loading="loadingEmployee"
                        :placeholder="$t('bankProfile.searchEmployee')"
                        :filter-by-label="false"
                        :filter-method="filterEmployee"
                        @on-change="onEmployeeSelected"
                    >
                        <Option
                            v-for="emp in employeeOptions"
                            :key="emp.employee_id"
                            :value="emp.employee_id"
                            :label="emp.employee_name_en || emp.employee_name_kh"
                        >
                            <div class="tw-flex tw-items-center tw-gap-2 tw-py-1">
                                <span class="tw-font-mono tw-text-gray-400 tw-text-xs tw-w-16 tw-flex-shrink-0">{{ emp.card_id }}</span>
                                <span class="tw-text-gray-800">{{ emp.employee_name_en }}</span>
                                <span v-if="emp.employee_name_kh" class="tw-text-gray-400 tw-text-xs">{{ emp.employee_name_kh }}</span>
                            </div>
                        </Option>
                    </Select>
                    <p class="tw-text-gray-400 tw-text-xs tw-mt-1">
                        <i class="fas fa-info-circle tw-mr-1"></i>{{ $t('bankProfile.employeeHint') }}
                    </p>
                </div>

                <!-- Right: Account fields -->
                <div class="col-md-7 tw-pl-2">
                    <label class="required form-col-label control-label tw-block tw-mb-1">
                        {{ $t('bankProfile.accountNo') }}
                    </label>
                    <Input
                        ref="accountNoInput"
                        v-model="form.account_no"
                        size="large"
                        :placeholder="$t('bankProfile.accountNo')"
                        :class="{ 'is-invalid': errors.has('account_no') }"
                        @on-enter="focusAccountName"
                    />
                    <div class="invalid-feedback tw-text-xs" v-if="errors.has('account_no')">
                        {{ errors.first('account_no') }}
                    </div>

                    <label class="required form-col-label control-label tw-block tw-mb-1 tw-mt-3">
                        {{ $t('bankProfile.accountName') }}
                    </label>
                    <Input
                        ref="accountNameInput"
                        v-model="form.account_name"
                        size="large"
                        :placeholder="$t('bankProfile.accountName')"
                        :class="{ 'is-invalid': errors.has('account_name') }"
                        @on-enter="onSave"
                    />
                    <div class="invalid-feedback tw-text-xs" v-if="errors.has('account_name')">
                        {{ errors.first('account_name') }}
                    </div>
                </div>
            </div>

            <div class="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                <div class="row">
                    <!-- QR upload dropzone -->
                    <div class="col-md-7 tw-pr-2">
                        <label class="form-col-label control-label tw-block tw-mb-1">
                            {{ $t('bankProfile.qrImage') }}
                        </label>
                        <div
                            class="tw-relative tw-border-2 tw-border-dashed tw-rounded-lg tw-p-3 tw-flex tw-items-center tw-gap-3 tw-transition"
                            :class="[
                                dragging ? 'tw-border-blue-400 tw-bg-blue-50' : 'tw-border-gray-200',
                                uploadingQr ? 'tw-opacity-70' : ''
                            ]"
                            @dragover.prevent="dragging = true"
                            @dragleave.prevent="dragging = false"
                            @drop.prevent="onQrDrop"
                        >
                            <div
                                v-if="form.qr_image"
                                class="tw-relative tw-flex-shrink-0"
                            >
                                <img
                                    :src="form.qr_image"
                                    class="tw-w-20 tw-h-20 tw-object-cover tw-rounded tw-border tw-border-gray-200"
                                />
                                <a
                                    href="#"
                                    class="tw-absolute tw--top-2 tw--right-2 tw-w-5 tw-h-5 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs"
                                    @click.prevent="form.qr_image = null"
                                    v-tooltip="$t('delete')"
                                >
                                    <i class="fas fa-times" style="font-size:10px;"></i>
                                </a>
                            </div>
                            <div
                                v-else
                                class="tw-w-20 tw-h-20 tw-bg-gray-50 tw-rounded tw-border tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
                            >
                                <i class="fas fa-qrcode tw-text-gray-300" style="font-size:28px;"></i>
                            </div>

                            <div class="tw-flex-grow">
                                <label class="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-bg-blue-50 hover:tw-bg-blue-100 tw-rounded tw-cursor-pointer tw-text-blue-600 tw-text-sm">
                                    <i v-if="uploadingQr" class="fas fa-circle-notch fa-spin tw-mr-1"></i>
                                    <i v-else class="fas fa-cloud-upload-alt tw-mr-1"></i>
                                    {{ form.qr_image ? $t('bankProfile.replaceQr') : $t('bankProfile.uploadQr') }}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        class="tw-hidden"
                                        :disabled="uploadingQr"
                                        @change="onQrChange"
                                    />
                                </label>
                                <p class="tw-text-gray-400 tw-text-xs tw-mt-1 tw-mb-0">
                                    {{ $t('bankProfile.qrHint') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Receive Transfer -->
                    <div class="col-md-5 tw-pl-2">
                        <label class="form-col-label control-label tw-block tw-mb-1">
                            {{ $t('bankProfile.isReceiveCashTransfer') }}
                        </label>
                        <div class="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-bg-gray-50 tw-rounded tw-border tw-border-gray-100">
                            <i-switch v-model="form.is_receive_cash_transfer" size="large" />
                            <div class="tw-flex-grow">
                                <div class="tw-text-sm tw-font-medium">
                                    {{ form.is_receive_cash_transfer ? $t('bankProfile.transferOn') : $t('bankProfile.transferOff') }}
                                </div>
                                <div class="tw-text-xs tw-text-gray-500">
                                    {{ $t('bankProfile.transferHint') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="tw-mt-5 tw-pt-4 tw-border-t tw-border-gray-100 tw-flex tw-items-center tw-justify-end">
                <div class="tw-flex tw-gap-2">
                    <ts-button class="btn-gray" @click.prevent="onCancel">{{ $t('cancel') }}</ts-button>
                    <ts-button
                        v-if="!isEdit"
                        color="info"
                        ghost
                        :waiting="saving && savingMode === 'add'"
                        :disabled="saving"
                        @click.prevent="onSaveAndAdd"
                    >
                        <i class="fas fa-plus tw-mr-1"></i>{{ $t('bankProfile.saveAndAdd') }}
                    </ts-button>
                    <ts-button
                        color="primary"
                        :waiting="saving && savingMode === 'save'"
                        :disabled="saving"
                        @click.prevent="onSave"
                    >
                        {{ $t('save') }}
                    </ts-button>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script>
import { Errors } from "form-backend-validation";

const emptyForm = () => ({
    account_id: null,
    employee_id: null,
    account_no: "",
    account_name: "",
    qr_image: null,
    is_receive_cash_transfer: false
});

const AVATAR_COLORS = [
    "#e74c3c", "#3498db", "#2ecc71", "#f39c12", "#9b59b6",
    "#1abc9c", "#e67e22", "#34495e", "#16a085", "#2980b9"
];

export default {
    name: "BankAccountFormModal",
    props: {
        value: { type: Boolean, default: false },
        bankId: { type: [Number, String], default: null },
        bankName: { type: String, default: "" },
        account: { type: Object, default: null }
    },
    data() {
        return {
            form: emptyForm(),
            saving: false,
            savingMode: null,
            uploadingQr: false,
            dragging: false,
            employeeOptions: [],
            selectedEmployee: null,
            loadingEmployee: false,
            errors: new Errors()
        };
    },
    computed: {
        isEdit() {
            return !!(this.form && this.form.account_id);
        },
        bankInitial() {
            return (this.bankName || "?").trim().charAt(0).toUpperCase();
        },
        avatarColor() {
            const name = this.bankName || "";
            let hash = 0;
            for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
            return AVATAR_COLORS[hash % AVATAR_COLORS.length];
        }
    },
    watch: {
        value(open) {
            if (open) {
                this.reset();
                this.loadEmployees();
                this.$nextTick(() => this.focusFirstField());
            }
        }
    },
    methods: {
        reset() {
            this.errors = new Errors();
            this.employeeOptions = [];
            this.selectedEmployee = null;
            this.dragging = false;
            if (this.account) {
                this.form = {
                    account_id: this.account.account_id || null,
                    employee_id: this.account.employee_id || null,
                    account_no: this.account.account_no || "",
                    account_name: this.account.account_name || "",
                    qr_image: this.account.qr_image || null,
                    is_receive_cash_transfer: !!this.account.is_receive_cash_transfer
                };
                if (this.account.employee) {
                    this.selectedEmployee = this.account.employee;
                    this.employeeOptions = [this.account.employee];
                }
            } else {
                this.form = emptyForm();
            }
        },
        focusFirstField() {
            const ref = this.isEdit ? this.$refs.accountNoInput : this.$refs.employeeSelect;
            if (ref && typeof ref.focus === "function") ref.focus();
        },
        focusAccountName() {
            const ref = this.$refs.accountNameInput;
            if (ref && typeof ref.focus === "function") ref.focus();
        },
        loadEmployees() {
            this.loadingEmployee = true;
            this.$store
                .dispatch("finance/bankProfile/searchEmployees", "")
                .then(data => {
                    const results = data || [];
                    const selected = this.selectedEmployee;
                    if (selected && !results.some(e => e.employee_id === selected.employee_id)) {
                        this.employeeOptions = [selected, ...results];
                    } else {
                        this.employeeOptions = results;
                    }
                })
                .finally(() => { this.loadingEmployee = false; });
        },
        filterEmployee(query, option) {
            if (!query) return true;
            const q = query.toLowerCase();
            const props = option.componentOptions && option.componentOptions.propsData;
            if (!props) return false;
            const emp = this.employeeOptions.find(e => e.employee_id === props.value);
            if (!emp) return false;
            return (
                (emp.employee_name_en || "").toLowerCase().includes(q) ||
                (emp.employee_name_kh || "").toLowerCase().includes(q) ||
                (emp.card_id || "").toString().toLowerCase().includes(q)
            );
        },
        onEmployeeSelected(id) {
            // Do NOT clear selectedEmployee on null — iView emits null internally
            // when it can't find the current value in a replaced option list.
            if (!id) return;
            const emp = this.employeeOptions.find(e => e.employee_id === id);
            if (!emp) return;
            this.selectedEmployee = emp;
            this.form.account_name = (emp.employee_name_en || emp.employee_name_kh || "").toUpperCase();
            this.$nextTick(() => {
                const ref = this.$refs.accountNoInput;
                if (ref && typeof ref.focus === "function") ref.focus();
            });
        },
        onCancel() {
            this.$emit('input', false);
            this.$emit('cancel');
        },
        onSave() {
            this.savingMode = 'save';
            this.doSave(false);
        },
        onSaveAndAdd() {
            if (this.isEdit) return;
            this.savingMode = 'add';
            this.doSave(true);
        },
        doSave(keepOpen) {
            this.errors = new Errors();
            this.saving = true;
            const payload = { ...this.form, bank_id: this.bankId };
            const action = this.isEdit
                ? this.$store.dispatch("finance/bankProfile/updateAccount", {
                      accountId: this.form.account_id,
                      data: payload
                  })
                : this.$store.dispatch("finance/bankProfile/storeAccount", {
                      bankId: this.bankId,
                      data: payload
                  });

            action
                .then(response => {
                    this.$emit('saved', { response, isEdit: this.isEdit });
                    if (!response || response.message_id == 0) {
                        if (keepOpen) {
                            this.form = emptyForm();
                            this.errors = new Errors();
                            this.$nextTick(() => this.focusFirstField());
                        } else {
                            this.$emit('input', false);
                        }
                    }
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.$emit('error', error);
                })
                .finally(() => {
                    this.saving = false;
                    this.savingMode = null;
                });
        },
        onQrChange(event) {
            const file = event.target.files[0];
            if (file) this.uploadQr(file);
            event.target.value = "";
        },
        onQrDrop(event) {
            this.dragging = false;
            const file = event.dataTransfer.files && event.dataTransfer.files[0];
            if (file) this.uploadQr(file);
        },
        uploadQr(file) {
            // Client-side guard: 5MB limit (matches server rule `image|max:5120`).
            const maxBytes = 5 * 1024 * 1024;
            if (file.size > maxBytes) {
                this.notice({
                    type: "error",
                    text: this.$t("bankProfile.qrTooLarge")
                });
                return;
            }
            if (!file.type || !file.type.startsWith("image/")) {
                this.notice({
                    type: "error",
                    text: this.$t("bankProfile.qrInvalidType")
                });
                return;
            }
            const formData = new FormData();
            formData.append("photo", file);
            this.uploadingQr = true;
            this.$store
                .dispatch("finance/bankProfile/qrUpload", formData)
                .then(path => { this.form.qr_image = `/api/files?filePath=${path}`; })
                .catch(err => {
                    const msg =
                        (err && err.errors && err.errors.photo && err.errors.photo[0]) ||
                        (err && err.message) ||
                        this.$t("bankProfile.qrUploadFailed");
                    this.notice({ type: "error", text: msg });
                })
                .finally(() => { this.uploadingQr = false; });
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "BANK PROFILE",
                desc: not.text
            });
        },
    }
};
</script>
